import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { SearchInput } from "@/components/SearchInput";
import { StatusDot } from "@/components/StatusDot";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MailSyncBar } from "@/features/companies/MailSyncBar";
import { SevdeskCell } from "@/features/companies/SevdeskCell";
import { STATUS_LABEL } from "@/features/companies/status";
import { CustomerAddDialog } from "@/features/customers/CustomerAddDialog";
import { CustomerLinkDialog } from "@/features/customers/CustomerLinkDialog";
import { get } from "@/lib/api";
import { eur, today } from "@/lib/format";
import { isStale, relativeDays } from "@/lib/relative";
import { searchKey } from "@/lib/search";
import type { CompaniesResponse, CompanyRow, CompanyStatus } from "@/lib/types";

const TABS: [CompanyStatus | "all", string][] = [["customer", "Kunden"], ["lead", "Leads"], ["former", "Ehemalig"], ["all", "Alle"]];

export function Firmen() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("status");
  const status = (TABS.some(([k]) => k === raw) ? raw : "customer") as CompanyStatus | "all";
  const q = sp.get("q") ?? "";
  const setParam = (key: string, value: string, fallback: string) => {
    const next = new URLSearchParams(sp);
    if (!value || value === fallback) next.delete(key);
    else next.set(key, value);
    setSp(next, { replace: true });
  };

  const data = useQuery({ queryKey: ["companies", status], queryFn: () => get<CompaniesResponse>(`/api/companies?status=${status}`) });
  const rows = useMemo(() => {
    const k = searchKey(q);
    return (data.data?.companies ?? []).filter((c) => !k || [c.name, c.primaryContact?.name ?? "", ...c.domains].some((h) => searchKey(h).includes(k)));
  }, [data.data, q]);
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState<CompanyRow | null>(null);
  const now = today();

  return (
    <div className="space-y-5">
      {/* Custom header (not PageHeader): MailSyncBar's status text + button need to drop to their own row on
          narrow screens so nothing overflows horizontally. The order-* classes keep desktop unchanged — title,
          then MailSyncBar, then the "Firma anlegen…" button, all in one row, MailSyncBar's row pushed right. */}
      <div className="flex min-h-9 flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-lg font-semibold text-balance">Firmen</h1>
        <Button size="sm" className="order-1 sm:order-2" onClick={() => setAdding(true)}><Plus />Firma anlegen…</Button>
        <MailSyncBar className="order-2 w-full sm:order-1 sm:ml-auto sm:w-auto" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={status} onValueChange={(v) => setParam("status", v, "customer")}>
          <TabsList aria-label="Status">{TABS.map(([k, label]) => <TabsTrigger key={k} value={k}>{label}</TabsTrigger>)}</TabsList>
        </Tabs>
        <SearchInput className="w-full sm:ml-auto sm:w-60" value={q} onChange={(v) => setParam("q", v, "")} />
      </div>
      {data.data?.warnings.map((w) => <p key={w} className="text-sm text-muted-foreground">{w}</p>)}
      {data.isError && <p className="text-sm text-destructive">{data.error.message}</p>}

      <Table className="table-fixed">
        <colgroup>
          <col /><col style={{ width: "11rem" }} /><col className="max-sm:hidden" style={{ width: "8rem" }} />
          <col className="max-md:hidden" style={{ width: "28%" }} /><col style={{ width: "2.5rem" }} />
        </colgroup>
        <TableHeader>
          <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
            <TableHead>Firma</TableHead>
            <TableHead>Letzter Kontakt</TableHead>
            <TableHead className="text-right max-sm:hidden">Offen</TableHead>
            <TableHead className="max-md:hidden">sevDesk</TableHead>
            <TableHead><span className="sr-only">Öffnen</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.isPending ? <SkeletonRows columns={5} rows={6} /> : rows.length === 0 ? (
            <EmptyRow columns={5}>{q ? `Keine Firma zu „${q}“.` : "Keine Firmen in dieser Ansicht."}</EmptyRow>
          ) : rows.map((c) => {
            const open = () => navigate(`/firmen/${c.id}`);
            return (
              <TableRow key={c.id} tabIndex={0} aria-label={`${c.name} öffnen`} onClick={open}
                onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) open(); }}
                className="group h-14 cursor-pointer outline-none focus-visible:bg-muted/60">
                <TableCell className="overflow-hidden">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium" title={c.name}>{c.name}</span>
                    {status === "all" && c.status !== "customer" && <Badge variant="secondary" className="shrink-0">{STATUS_LABEL[c.status]}</Badge>}
                  </span>
                  {c.primaryContact && <span className="block truncate text-xs text-muted-foreground">{c.primaryContact.name}</span>}
                </TableCell>
                <TableCell className="tabular-nums">
                  {isStale(c.lastContact, now)
                    ? <StatusDot tone="open">{relativeDays(c.lastContact, now)}</StatusDot>
                    : <span className="text-muted-foreground">{relativeDays(c.lastContact, now)}</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums max-sm:hidden">{c.openEur > 0 ? eur(c.openEur) : <span className="text-muted-foreground">–</span>}</TableCell>
                <TableCell className="overflow-hidden max-md:hidden"><SevdeskCell c={c} reachable={data.data?.warnings.length === 0} onLink={() => setLinking(c)} /></TableCell>
                <TableCell className="text-muted-foreground/60 group-hover:text-foreground"><ChevronRight aria-hidden className="size-4" /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <CustomerAddDialog open={adding} onClose={() => setAdding(false)} />
      <CustomerLinkDialog customer={linking} onClose={() => setLinking(null)} />
    </div>
  );
}
